<template>
  <div class="modal fade" id="receiptModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header text-white">
          <h5 class="modal-title">
            <i class="bx bx-receipt me-2"></i>Payment Receipt
          </h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" @click="closeModal"></button>
        </div>

        <div class="modal-body p-0">
          <!-- Loading state -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3">Loading receipt...</p>
          </div>

          <!-- Receipt content -->
          <div v-else-if="paymentDetails" class="receipt-container" id="receiptContent">
            <!-- Compact Receipt Header -->
            <div class="receipt-header">
              <div class="d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center">
                  <div class="logo me-3">CBHI</div>
                  <div>
                    <h6 class="mb-0 text-white">Community Based Health Insurance</h6>
                    <p class="mb-0 small">Ministry of Health - Ethiopia</p>
                  </div>
                </div>
                <div class="text-end">
                  <div class="receipt-number">{{ paymentDetails.payment.receipt_number }}</div>
                  <small>{{ formatDate(paymentDetails.payment.payment_date) }}</small>
                </div>
              </div>
            </div>

            <!-- Compact Receipt Body -->
            <div class="receipt-body">
              <!-- Member & Payment Info in 2 columns -->
              <div class="row mb-2">
                <div class="col-md-6">
                  <h6 class="section-title">Member Information</h6>
                  <table class="table table-sm table-borderless">
                    <tr>
                      <td class="text-muted">CBHI Number:</td>
                      <td><strong>{{ paymentDetails.member.cbhi_number }}</strong></td>
                    </tr>
                    <tr>
                      <td class="text-muted">Name:</td>
                      <td><strong>{{ memberFullName }}</strong></td>
                    </tr>
                    <tr>
                      <td class="text-muted">Phone:</td>
                      <td>{{ paymentDetails.member.phone_number || '-' }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Location:</td>
                      <td>{{ paymentDetails.location.woreda_name }}, {{ paymentDetails.location.zone_name }}</td>
                    </tr>
                  </table>
                </div>

                <div class="col-md-6">
                  <h6 class="section-title">Payment Information</h6>
                  <table class="table table-sm table-borderless">
                    <tr>
                      <td class="text-muted">Method:</td>
                      <td><strong>{{ paymentDetails.payment.method_name }}</strong></td>
                    </tr>
                    <tr>
                      <td class="text-muted">Reference:</td>
                      <td>{{ paymentDetails.payment.payment_reference }}</td>
                    </tr>
                    <tr v-if="paymentDetails.payment.payment_type === 'MOBILE'">
                      <td class="text-muted">Mobile:</td>
                      <td>{{ paymentDetails.payment.mobileNumber }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Collected By:</td>
                      <td>{{ paymentDetails.payment.collected_by }}</td>
                    </tr>
                  </table>
                </div>
              </div>

              <!-- Payment Summary Table -->
              <div class="mb-2">
                <h6 class="section-title">Payment Summary</h6>
                <table class="table table-bordered table-sm">
                  <thead class="table-light">
                  <tr>
                    <th>Description</th>
                    <th class="text-center">Beneficiaries</th>
                    <th class="text-end">Amount (ETB)</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr>
                    <td>Annual CBHI Premium ({{ paymentDetails.payment.payment_year }})</td>
                    <td class="text-center">{{ paymentDetails.totalBeneficiaries }}</td>
                    <td class="text-end"><strong>{{ formatCurrency(paymentDetails.expectedAnnualPremium) }}</strong></td>
                  </tr>
                  <tr class="table-success">
                    <td><strong>Total Paid to Date</strong></td>
                    <td class="text-center">-</td>
                    <td class="text-end"><strong>{{ formatCurrency(paymentDetails.totalPaidForYear) }}</strong></td>
                  </tr>
                  <tr class="table-info">
                    <td>This Payment</td>
                    <td class="text-center">-</td>
                    <td class="text-end">{{ formatCurrency(paymentDetails.payment.amount) }}</td>
                  </tr>
                  <tr :class="balanceRowClass">
                    <td><strong>{{ balanceDescription }}</strong></td>
                    <td class="text-center">-</td>
                    <td class="text-end"><strong>{{ balanceAmountText }}</strong></td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <!-- Status & Coverage in 2 columns -->
              <div class="row mb-2">
                <div class="col-md-6">
                  <h6 class="section-title">Coverage Status</h6>
                  <div class="status-card" :class="paymentStatusClass">
                    <div class="d-flex align-items-center">
                      <i :class="paymentStatusIcon" class="fs-4 me-3"></i>
                      <div>
                        <div class="fw-bold">{{ paymentStatusText }}</div>
                        <small>{{ getCoverageText() }}</small>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-6">
                  <h6 class="section-title">Plan Details</h6>
                  <table class="table table-sm table-borderless">
                    <tr>
                      <td class="text-muted">Plan:</td>
                      <td><strong>{{ paymentDetails.plan.plan_name }}</strong></td>
                    </tr>
                    <tr>
                      <td class="text-muted">Coverage Limit:</td>
                      <td>ETB {{ formatCurrency(paymentDetails.plan.annual_coverage_limit) }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Visit Limit:</td>
                      <td>ETB {{ formatCurrency(paymentDetails.plan.per_visit_limit) }}</td>
                    </tr>
                  </table>
                </div>
              </div>

              <!-- Balance Notice (if applicable) -->
              <div v-if="hasOutstandingBalance || isOverpaid" class="balance-notice" :class="balanceNoticeClass">
                <div class="d-flex align-items-start">
                  <i :class="balanceNoticeIcon" class="fs-5 me-2 mt-1"></i>
                  <div>
                    <strong>{{ balanceNoticeTitle }}</strong>
                    <p class="mb-0 small">{{ balanceNoticeMessage }}</p>
                  </div>
                </div>
              </div>

              <!-- Notes (if any) -->
              <div v-if="paymentDetails.payment.notes" class="mb-2">
                <h6 class="section-title">Notes</h6>
                <p class="notes-text">{{ paymentDetails.payment.notes }}</p>
              </div>
            </div>

            <!-- Compact Footer -->
            <div class="receipt-footer">
              <div class="row">
                <div class="col-md-8">
                  <div class="signature-section">
                    <div class="row">
                      <div class="col-6">
                        <div class="signature-line">
                          <strong style="font-size: 10px;">{{ paymentDetails.payment.collected_by }}</strong>
                          <div class="small text-muted">Received By</div>
                        </div>
                      </div>
                      <div class="col-6">
                        <div class="signature-line">
                          <div class="border-bottom mb-1" style="height: 12px;"></div>
                          <div class="small text-muted">Member Signature</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-4 text-end">
                  <div class="stamp-area">
                    <div class="text-muted small" style="font-size: 9px;">Official Stamp</div>
                    <div class="border rounded p-2 mt-1" style="height: 45px;"></div>
                  </div>
                </div>
              </div>

              <!-- Print info -->
              <div class="print-info mt-2 pt-2 border-top">
                <div class="row text-center small text-muted">
                  <div class="col-md-4">Generated: {{ formatDateTime(new Date()) }}</div>
                  <div class="col-md-4">Hotline: 8335</div>
                  <div class="col-md-4">www.moh.gov.et</div>
                </div>
                <p class="text-center mb-0 mt-1 small">This is a computer-generated receipt. Please keep it for your records.</p>
              </div>
            </div>
          </div>

          <!-- Error state -->
          <div v-else class="text-center py-5">
            <i class="bx bx-error-circle display-1 text-danger"></i>
            <p class="text-muted">Failed to load receipt data</p>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="closeModal">
            <i class="bx bx-x me-1"></i>Close
          </button>
          <button type="button" class="btn btn-primary" @click="downloadReceipt" :disabled="!paymentDetails">
            <i class="bx bx-download me-1"></i>Download PDF
          </button>
          <button type="button" class="btn btn-outline-primary" @click="printReceipt" :disabled="!paymentDetails">
            <i class="bx bx-printer me-1"></i>Print
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "ReceiptModal",

  data() {
    return {
      modalInstance: null,
      paymentDetails: null,
      loading: false
    };
  },

  computed: {
    // ✅ FIXED: API only returns full_name (single field)
    memberFullName() {
      if (!this.paymentDetails?.member) return 'N/A';
      return this.paymentDetails.member.full_name || 'N/A';
    },

    balanceAmount() {
      if (!this.paymentDetails?.payment_summary) return 0;
      return this.paymentDetails.payment_summary.balance_due;
    },

    isFullyPaid() {
      return Math.abs(this.balanceAmount) < 0.01;
    },

    hasOutstandingBalance() {
      return this.balanceAmount > 0.01;
    },

    isOverpaid() {
      return this.balanceAmount < -0.01;
    },

    balanceDescription() {
      if (this.isFullyPaid) return 'Payment Status';
      if (this.hasOutstandingBalance) return 'Outstanding Balance';
      if (this.isOverpaid) return 'Overpayment';
      return 'Balance';
    },

    balanceAmountText() {
      if (this.isFullyPaid) return 'PAID IN FULL';
      if (this.hasOutstandingBalance) return `ETB ${this.formatCurrency(this.balanceAmount)}`;
      if (this.isOverpaid) return `ETB ${this.formatCurrency(Math.abs(this.balanceAmount))} Credit`;
      return 'ETB 0';
    },

    balanceRowClass() {
      if (this.isFullyPaid) return 'table-success';
      if (this.hasOutstandingBalance) return 'table-warning';
      if (this.isOverpaid) return 'table-info';
      return '';
    },

    paymentStatusText() {
      if (this.isFullyPaid) return 'PAID IN FULL';
      if (this.hasOutstandingBalance) return 'PARTIAL PAYMENT';
      if (this.isOverpaid) return 'OVERPAID';
      return 'PAYMENT COMPLETE';
    },

    paymentStatusClass() {
      if (this.isFullyPaid) return 'status-paid';
      if (this.hasOutstandingBalance) return 'status-partial';
      if (this.isOverpaid) return 'status-overpaid';
      return 'status-paid';
    },

    paymentStatusIcon() {
      if (this.isFullyPaid) return 'bx bx-check-circle';
      if (this.hasOutstandingBalance) return 'bx bx-exclamation-triangle';
      if (this.isOverpaid) return 'bx bx-info-circle';
      return 'bx bx-check-circle';
    },

    balanceNoticeClass() {
      if (this.hasOutstandingBalance) return 'notice-warning';
      if (this.isOverpaid) return 'notice-info';
      return 'notice-success';
    },

    balanceNoticeIcon() {
      if (this.hasOutstandingBalance) return 'bx bx-exclamation-triangle';
      if (this.isOverpaid) return 'bx bx-info-circle';
      return 'bx bx-check-circle';
    },

    balanceNoticeTitle() {
      if (this.hasOutstandingBalance) return 'Outstanding Balance Notice';
      if (this.isOverpaid) return 'Overpayment Notice';
      return 'Payment Complete';
    },

    balanceNoticeMessage() {
      if (!this.paymentDetails) return '';

      const totalPaid = this.paymentDetails.totalPaidForYear;
      const expectedPremium = this.paymentDetails.expectedAnnualPremium;
      const balance = this.balanceAmount;
      const year = this.paymentDetails.payment.payment_year;

      if (this.hasOutstandingBalance) {
        return `Member needs to pay an additional ETB ${this.formatCurrency(balance)} to complete the annual premium for ${year}. Coverage will activate once full payment is received.`;
      }
      if (this.isOverpaid) {
        return `Member has paid ETB ${this.formatCurrency(Math.abs(balance))} more than the required annual premium. This amount can be applied to future payments or refunded as per policy.`;
      }
      return `Full payment received. Coverage is active for ${year}.`;
    }
  },

  mounted() {
    if (typeof window !== 'undefined' && window.bootstrap) {
      this.modalInstance = new window.bootstrap.Modal(document.getElementById('receiptModal'));

      document.getElementById('receiptModal').addEventListener('hidden.bs.modal', () => {
        this.$emit('modal-closed');
      });
    }
  },

  methods: {
    async openModal(payment) {
      this.loading = true;
      this.paymentDetails = null;

      if (this.modalInstance) {
        this.modalInstance.show();
      }

      try {
        if (payment && (payment.payment_id || payment.paymentId)) {
          const data = {
            "function": "getPaymentDetails",
            "paymentId": payment.payment_id || payment.paymentId
          };

          const res = await executeJson(data, authUrl);
          if (res.data.success) {
            this.paymentDetails = res.data.data;
          } else {
            console.error('Failed to load payment details:', res.data.message);
          }
        }
      } catch (error) {
        console.error('Error loading payment details:', error);
      } finally {
        this.loading = false;
      }
    },

    closeModal() {
      if (this.modalInstance) {
        this.modalInstance.hide();
      }
      this.$emit('modal-closed');
      this.paymentDetails = null;
    },

    getCoverageStartDate() {
      if (!this.paymentDetails?.payment) return '';
      const year = this.paymentDetails.payment.payment_year;
      return `${year}-01-01`;
    },

    getCoverageEndDate() {
      if (!this.paymentDetails?.payment) return '';
      const year = this.paymentDetails.payment.payment_year;
      return `${year}-12-31`;
    },

    getCoverageText() {
      if (this.isFullyPaid) {
        return `Coverage: ${this.formatDate(this.getCoverageStartDate())} to ${this.formatDate(this.getCoverageEndDate())}`;
      } else {
        return `Coverage pending: ETB ${this.formatCurrency(this.balanceAmount)} remaining`;
      }
    },

    printReceipt() {
      const printWindow = window.open('', '_blank');
      const receiptContent = document.getElementById('receiptContent').outerHTML;

      const printDocument = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>CBHI Payment Receipt</title>
          <style>
            ${this.getPrintStyles()}
          </style>
        </head>
        <body>
          ${receiptContent}
        </body>
        </html>
      `;

      printWindow.document.write(printDocument);
      printWindow.document.close();

      setTimeout(() => {
        printWindow.print();
      }, 500);
    },

    downloadReceipt() {
      this.printReceipt();
    },

    getPrintStyles() {
      return `
        @media print {
          body { margin: 0; font-family: Arial, sans-serif; font-size: 10px; }
          @page { margin: 12mm 15mm; size: A4; }
          .no-print { display: none; }
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Arial', sans-serif;
          color: #333;
          line-height: 1.4;
        }

        .receipt-container {
          max-width: 100%;
          margin: 0 auto;
          background: white;
        }

        .receipt-header {
          background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
          color: white;
          padding: 18px 20px;
          margin-bottom: 5px;
        }

        .logo {
          width: 45px;
          height: 45px;
          background: white;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #1e3a8a;
          font-weight: bold;
          font-size: 16px;
        }

        .receipt-number {
          background: rgba(255,255,255,0.2);
          padding: 4px 8px;
          border-radius: 15px;
          font-size: 11px;
        }

        .receipt-body {
          padding: 18px 20px;
        }

        .section-title {
          font-size: 12px;
          font-weight: bold;
          color: #1e3a8a;
          margin-bottom: 6px;
          margin-top: 8px;
          border-bottom: 1.5px solid #3b82f6;
          padding-bottom: 3px;
        }

        .table {
          width: 100%;
          margin-bottom: 10px;
        }

        .table th, .table td {
          padding: 5px 6px;
          text-align: left;
          border-bottom: 1px solid #e5e7eb;
          font-size: 10px;
          line-height: 1.3;
        }

        .table-bordered {
          border: 1px solid #dee2e6;
        }

        .table-bordered th, .table-bordered td {
          border: 1px solid #dee2e6;
        }

        .table-light {
          background-color: #f8f9fa;
        }

        .table-success {
          background-color: #d1e7dd;
        }

        .table-info {
          background-color: #d1ecf1;
        }

        .table-warning {
          background-color: #fff3cd;
        }

        .table-sm th, .table-sm td {
          padding: 4px;
          font-size: 10px;
        }

        .status-card {
          padding: 10px;
          border-radius: 5px;
          border: 1.5px solid;
          margin-bottom: 8px;
        }

        .status-paid {
          background: #d1e7dd;
          border-color: #22c55e;
          color: #166534;
        }

        .status-partial {
          background: #fff3cd;
          border-color: #f59e0b;
          color: #92400e;
        }

        .status-overpaid {
          background: #dbeafe;
          border-color: #3b82f6;
          color: #1e40af;
        }

        .balance-notice {
          padding: 10px;
          border-radius: 5px;
          margin: 10px 0;
          font-size: 10px;
        }

        .notice-warning {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #7f1d1d;
        }

        .notice-info {
          background: #eff6ff;
          border: 1px solid #bfdbfe;
          color: #1e40af;
        }

        .notice-success {
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          color: #166534;
        }

        .receipt-footer {
          background: #f9fafb;
          padding: 12px 20px;
          border-top: 1px solid #e5e7eb;
          margin-top: 5px;
        }

        .signature-line {
          margin-bottom: 6px;
          font-size: 10px;
        }

        .stamp-area {
          text-align: center;
        }

        .print-info {
          font-size: 9px;
          color: #6b7280;
          line-height: 1.3;
        }

        .notes-text {
          background: #f9fafb;
          padding: 6px 8px;
          border-radius: 4px;
          font-style: italic;
          color: #4b5563;
          font-size: 9px;
          line-height: 1.3;
        }

        .text-end { text-align: right; }
        .text-center { text-align: center; }
        .fw-bold { font-weight: bold; }
        .small { font-size: 0.875em; }
        .fs-4 { font-size: 1.1rem; }
        .fs-5 { font-size: 0.9rem; }

        .mb-0 { margin-bottom: 0; }
        .mb-1 { margin-bottom: 0.25rem; }
        .mb-2 { margin-bottom: 0.5rem; }
        .mb-3 { margin-bottom: 0.75rem; }
        .mt-1 { margin-top: 0.25rem; }
        .mt-2 { margin-top: 0.5rem; }
        .me-2 { margin-right: 0.5rem; }
        .me-3 { margin-right: 0.75rem; }
        .pt-2 { padding-top: 0.5rem; }
        .p-2 { padding: 0.5rem; }

        .d-flex { display: flex; }
        .align-items-center { align-items: center; }
        .align-items-start { align-items: flex-start; }
        .justify-content-between { justify-content: space-between; }

        .row {
          display: flex;
          flex-wrap: wrap;
          margin-right: -0.5rem;
          margin-left: -0.5rem;
        }

        .col-md-4, .col-md-6, .col-md-8, .col-6 {
          padding-right: 0.5rem;
          padding-left: 0.5rem;
        }

        .col-md-4 { flex: 0 0 auto; width: 33.33333333%; }
        .col-md-6 { flex: 0 0 auto; width: 50%; }
        .col-md-8 { flex: 0 0 auto; width: 66.66666667%; }
        .col-6 { flex: 0 0 auto; width: 50%; }

        .border { border: 1px solid #dee2e6; }
        .border-bottom { border-bottom: 1px solid #dee2e6; }
        .border-top { border-top: 1px solid #dee2e6; }
        .rounded { border-radius: 0.25rem; }
        .text-muted { color: #6c757d; }

        h6 { font-size: 13px; margin: 0; }

        @media print {
          .receipt-container {
            min-height: 100%;
            display: flex;
            flex-direction: column;
          }

          .receipt-body {
            flex: 1;
          }

          .receipt-footer {
            margin-top: auto;
          }
        }
      `;
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    },

    formatDateTime(date) {
      if (!date) return '';
      return new Date(date).toLocaleString('en-US', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat().format(amount || 0);
    }
  }
};
</script>

<style scoped>
.receipt-container {
  background: white;
  color: #333;
}

.receipt-header {
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  color: white;
  padding: 15px;
}

.logo {
  width: 45px;
  height: 45px;
  background: white;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #1e3a8a;
  font-weight: bold;
  font-size: 16px;
}

.receipt-number {
  background: rgba(255,255,255,0.2);
  padding: 4px 8px;
  border-radius: 15px;
  font-size: 11px;
}

.receipt-body {
  padding: 15px;
}

.section-title {
  font-size: 13px;
  font-weight: bold;
  color: #1e3a8a;
  margin-bottom: 8px;
  border-bottom: 2px solid #3b82f6;
  padding-bottom: 4px;
}

.status-card {
  padding: 12px;
  border-radius: 6px;
  border: 2px solid;
}

.status-paid {
  background: #d1e7dd;
  border-color: #22c55e;
  color: #166534;
}

.status-partial {
  background: #fff3cd;
  border-color: #f59e0b;
  color: #92400e;
}

.status-overpaid {
  background: #dbeafe;
  border-color: #3b82f6;
  color: #1e40af;
}

.balance-notice {
  padding: 12px;
  border-radius: 6px;
  margin: 12px 0;
}

.notice-warning {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #7f1d1d;
}

.notice-info {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1e40af;
}

.notice-success {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #166534;
}

.receipt-footer {
  background: #f9fafb;
  padding: 15px;
  border-top: 1px solid #e5e7eb;
}

.signature-line {
  margin-bottom: 8px;
}

.stamp-area {
  text-align: center;
}

.print-info {
  font-size: 9px;
  color: #6b7280;
}

.notes-text {
  background: #f9fafb;
  padding: 8px;
  border-radius: 4px;
  font-style: italic;
  color: #4b5563;
  font-size: 10px;
}

.modal-header.bg-primary {
  border-bottom: none;
}

.btn-close-white {
  filter: brightness(0) invert(1);
}

.table-sm th,
.table-sm td {
  padding: 0.2rem;
  font-size: 0.8rem;
}

.table-borderless td,
.table-borderless th {
  border: 0;
}

/* Responsive adjustments for modal */
@media (max-width: 768px) {
  .modal-dialog {
    margin: 1rem;
  }

  .receipt-body {
    padding: 12px;
  }

  .receipt-header {
    padding: 12px;
  }

  .section-title {
    font-size: 12px;
  }

  .table-sm th,
  .table-sm td {
    padding: 0.15rem;
    font-size: 0.75rem;
  }
}
</style>